import {
  Button,
  LinkButton,
  SearchWithFilter,
  Tabs,
} from '@enamelai/design-system'
import AddInvoiceModal from '@components/layout/Modals/AddInvoice/AddInvoiceModal.component'
import CreateInvoiceModal from '@components/layout/Modals/CreateInvoiceModal/CreateInvoiceModal.component'
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import { useSelector } from 'react-redux'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { RootState } from '@store/index'
import { hasBillingListNarrowed } from '@utils/billing.utils'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import {
  FIRST_LOAD_OVERLAY_MS,
  FIRST_LOAD_OVERLAY_STYLE,
  waitAtLeast,
} from '@utils/waitAtLeast.util'
import BillingSkeleton from '@pages/Billing/BillingSkeleton.page'
import useLedger, {
  type LedgerTableHooks,
  type UseLedgerHooks,
} from './Ledger/useLedger'
import useBilling, { BillingTabType } from './useBilling'
import './BillingStyle.css'

const TAB_SKELETON_DURATION_MS = 500
const BILLING_LOADING_CONTAINER = '.billing-page'

export type { LedgerTableHooks }

export interface BillingOutletContext {
  hooks: ReturnType<typeof useBilling> &
    Pick<
      UseLedgerHooks,
      | 'ledgerEntries'
      | 'loadingLedger'
      | 'patientARAnalytics'
      | 'insuranceARAnalytics'
      | 'arAgingReport'
      | 'handleCreateInvoiceFromLedger'
      | 'initialSelectedEntry'
    > &
    LedgerTableHooks & {
      tabFilterConfigs: ReturnType<typeof useBilling>['tabFilterConfigs'] & {
        ledger: UseLedgerHooks['ledgerFilterConfig']
      }
      handleCloseCreateInvoiceModal: () => void
      isPageReady: boolean
      isTabSkeleton: boolean
      suppressTableLoading: boolean
    }
}

const parseBillingTabFromPathname = (
  pathname: string,
): BillingTabType | null => {
  const parts = pathname.split('/').filter(Boolean)
  const maybeTab = parts[1] // /billing/<tab>
  if (Object.values(BillingTabType).includes(maybeTab as BillingTabType)) {
    return maybeTab as BillingTabType
  }
  return null
}

function BillingComponent(): React.ReactElement {
  const navigate = useNavigate()
  const location = useLocation()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const { previousPage } = (location.state as { previousPage?: unknown }) || {}
  const [pagePreviousState, setPagePreviousState] = useState(previousPage)
  const [isPageReady, setIsPageReady] = useState(false)
  const [isTabSkeleton, setIsTabSkeleton] = useState(false)
  const [tabSkeletonKey, setTabSkeletonKey] = useState(0)
  const isPageReadyRef = useRef(false)
  const firstLoadStartedAtRef = useRef(Date.now())
  const tabSkeletonStartedAtRef = useRef(Date.now())

  useEffect(() => {
    const nextPreviousPage = (
      location.state as { previousPage?: unknown } | null | undefined
    )?.previousPage
    if (nextPreviousPage) {
      setPagePreviousState(nextPreviousPage)
    }
  }, [location.state])

  const activeTabFromUrl = parseBillingTabFromPathname(location.pathname)
  const activeTab: BillingTabType = activeTabFromUrl ?? BillingTabType.Ledger
  const prevTabRef = useRef(activeTab)

  const billingHooks = useBilling({
    activeTab,
    onChangeTab: (tab) =>
      navigate(`/billing/${tab}`, {
        state: location.state,
      }),
  })

  const ledgerHooks = useLedger(
    billingHooks.searchQuery,
    billingHooks.filters,
    {
      activeTab,
      suppressTableLoading: !isPageReady || isTabSkeleton,
      onOpenCreateInvoiceModal: () =>
        billingHooks.setShowCreateInvoiceModal(true),
      onChangeToInvoiceTab: () =>
        billingHooks.handleChangeTab(BillingTabType.Invoices),
    },
  )

  const hooks: BillingOutletContext['hooks'] = {
    ...billingHooks,
    ledgerEntries: ledgerHooks.ledgerEntries,
    loadingLedger: ledgerHooks.loadingLedger,
    patientARAnalytics: ledgerHooks.patientARAnalytics,
    insuranceARAnalytics: ledgerHooks.insuranceARAnalytics,
    arAgingReport: ledgerHooks.arAgingReport,
    filteredLedger: ledgerHooks.filteredLedger,
    ledgerCount: ledgerHooks.ledgerCount,
    currentPage: ledgerHooks.currentPage,
    pageSize: ledgerHooks.pageSize,
    handlePageChange: ledgerHooks.handlePageChange,
    handlePageSizeChange: ledgerHooks.handlePageSizeChange,
    handleTableSortChange: ledgerHooks.handleTableSortChange,
    selectedLedger: ledgerHooks.selectedLedger,
    showModal: ledgerHooks.showModal,
    handleView: ledgerHooks.handleView,
    handleModalClose: ledgerHooks.handleModalClose,
    relatedLedgerEntries: ledgerHooks.relatedLedgerEntries,
    tabFilterConfigs: {
      ...billingHooks.tabFilterConfigs,
      ledger: ledgerHooks.ledgerFilterConfig,
    },
    handleCreateInvoiceFromLedger: ledgerHooks.handleCreateInvoiceFromLedger,
    initialSelectedEntry: ledgerHooks.initialSelectedEntry,
    handleCloseCreateInvoiceModal: () => {
      billingHooks.handleCloseCreateInvoiceModal()
      ledgerHooks.setInitialSelectedEntry(null)
    },
    handleCreateInvoice: () => {
      ledgerHooks.setInitialSelectedEntry(null)
      billingHooks.handleCreateInvoice()
    },
    isPageReady,
    isTabSkeleton,
    suppressTableLoading: !isPageReady || isTabSkeleton,
  }

  const tabItems = [
    { id: BillingTabType.Ledger, label: 'Ledger' },
    { id: BillingTabType.Invoices, label: 'Invoices' },
    { id: BillingTabType.Payments, label: 'Payments' },
    { id: BillingTabType.Transactions, label: 'Transactions' },
  ]

  const { filters: currentFilters, labels: currentFilterLabels } =
    hooks.tabFilterConfigs[hooks.activeTab]

  const invoiceCount = useSelector(
    (state: RootState) => state.billing.invoiceCount,
  )
  const loadingInvoices = useSelector(
    (state: RootState) => state.billing.loadingInvoices,
  )
  const transactionCount = useSelector(
    (state: RootState) => state.billing.transactionCount,
  )
  const loadingTransactions = useSelector(
    (state: RootState) => state.billing.loadingTransactions,
  )
  const paymentHistoryCount = useSelector(
    (state: RootState) => state.billing.paymentHistoryCount,
  )
  const loadingPaymentHistory = useSelector(
    (state: RootState) => state.billing.loadingPaymentHistory,
  )
  const selectedInvoices = useSelector(
    (state: RootState) => state.billing.selectedInvoices,
  )

  const listNarrowed = hasBillingListNarrowed(hooks.searchQuery, hooks.filters)
  const showLedgerEmptyCta =
    !hooks.loadingLedger && !listNarrowed && hooks.ledgerCount === 0
  const showInvoiceEmptyCta =
    !loadingInvoices && !listNarrowed && invoiceCount === 0
  const showTransactionEmptyCta =
    !loadingTransactions && !listNarrowed && transactionCount === 0
  const showPaymentEmptyCta =
    !loadingPaymentHistory &&
    paymentHistoryCount === 0 &&
    selectedInvoices.length === 0
  const disableCreateInvoice =
    (hooks.activeTab === BillingTabType.Ledger && showLedgerEmptyCta) ||
    (hooks.activeTab === BillingTabType.Invoices && showInvoiceEmptyCta) ||
    (hooks.activeTab === BillingTabType.Transactions && showTransactionEmptyCta)
  const disableAddInvoice =
    hooks.activeTab === BillingTabType.Payments && showPaymentEmptyCta

  const isActiveTabLoading =
    activeTab === BillingTabType.Ledger
      ? hooks.loadingLedger
      : activeTab === BillingTabType.Invoices
        ? loadingInvoices
        : activeTab === BillingTabType.Payments
          ? loadingPaymentHistory
          : loadingTransactions

  useLayoutEffect(() => {
    if (isPageReadyRef.current) return
    showLoading({
      container: BILLING_LOADING_CONTAINER,
      style: FIRST_LOAD_OVERLAY_STYLE,
    })
  }, [showLoading])

  useEffect(() => {
    if (isPageReadyRef.current) return
    if (isActiveTabLoading) return
    let cancelled = false
    void waitAtLeast(firstLoadStartedAtRef.current, FIRST_LOAD_OVERLAY_MS).then(
      () => {
        if (cancelled) return
        isPageReadyRef.current = true
        setIsPageReady(true)
        hideLoading(BILLING_LOADING_CONTAINER)
      },
    )
    return () => {
      cancelled = true
    }
  }, [isActiveTabLoading, hideLoading])

  useEffect(() => {
    return () => {
      hideLoading(BILLING_LOADING_CONTAINER)
    }
  }, [hideLoading])

  useLayoutEffect(() => {
    if (isPageReadyRef.current && prevTabRef.current !== activeTab) {
      hideLoading(BILLING_LOADING_CONTAINER)
      hideLoading('.ledger-table')
      hideLoading('.invoice-table')
      hideLoading('.payment-history-table')
      hideLoading('.transaction-table')
      tabSkeletonStartedAtRef.current = Date.now()
      setIsTabSkeleton(true)
      setTabSkeletonKey((key) => key + 1)
    }
    prevTabRef.current = activeTab
  }, [activeTab, hideLoading])

  useEffect(() => {
    if (!isTabSkeleton) return
    if (isActiveTabLoading) return

    let cancelled = false
    const hideWhenReady = (): void => {
      void waitAtLeast(
        tabSkeletonStartedAtRef.current,
        TAB_SKELETON_DURATION_MS,
      ).then(() => {
        if (cancelled) return
        setIsTabSkeleton(false)
      })
    }

    // Child tab fetch starts in useEffect. Wait a tick so we do not treat
    // "not loading yet" as "data is ready" and drop the skeleton too early.
    const awaitFetchStart = window.setTimeout(hideWhenReady, 50)
    return () => {
      cancelled = true
      window.clearTimeout(awaitFetchStart)
    }
  }, [isTabSkeleton, tabSkeletonKey, isActiveTabLoading])

  const previousPageBackLabel = (
    pagePreviousState as { backLabel?: unknown } | null
  )?.backLabel
  const backLabel =
    typeof previousPageBackLabel === 'string' && previousPageBackLabel.trim()
      ? previousPageBackLabel
      : 'Back to appointment'

  return (
    <>
      <Container
        fluid
        className={`billing-page padd-812${isPageReady ? ' is-ready' : ''}`}
      >
        {pagePreviousState ? (
          <LinkButton
            className="billing-back-link"
            leftIcon={<span className="mgc_left_line" />}
            onClick={() => {
              const previousPageState = pagePreviousState as { path: string }
              navigate(previousPageState.path, {
                state: {
                  ...(pagePreviousState as object),
                },
              })
            }}
          >
            {backLabel}
          </LinkButton>
        ) : null}
        <Row className="billing-page-header align-items-center justify-content-between">
          <Col md="auto">
            <Tabs
              items={tabItems}
              value={hooks.activeTab}
              onChange={(tabId) =>
                hooks.handleChangeTab(tabId as BillingTabType)
              }
            />
          </Col>

          <Col md="auto" className="d-flex align-items-center gap-2">
            <Button size="md" variant="secondary" onClick={hooks.handleExport}>
              Export
            </Button>

            {hooks.activeTab !== BillingTabType.Payments && (
              <SearchWithFilter
                placeholder="Search"
                value={hooks.searchQuery}
                onChange={hooks.handleSearchChange}
                filters={currentFilters}
                onFilterChange={hooks.handleFilterChange}
                filterLabels={currentFilterLabels}
              />
            )}

            {hooks.activeTab === BillingTabType.Payments ? (
              <Button
                size="md"
                variant="primary"
                onClick={hooks.handleAddInvoice}
                disabled={disableAddInvoice}
              >
                Add Invoice
              </Button>
            ) : (
              <Button
                size="md"
                variant="primary"
                onClick={hooks.handleCreateInvoice}
                disabled={disableCreateInvoice}
              >
                Create Invoice
              </Button>
            )}
          </Col>
          <div className="billing-tab-border my-2"></div>
        </Row>

        <Row className="billing-page-row">
          <Col>
            {isTabSkeleton ? <BillingSkeleton variant={activeTab} /> : null}
            <div
              className={`billing-tab-stage${
                isTabSkeleton ? ' billing-tab-stage--hidden' : ''
              }`}
            >
              <Outlet context={{ hooks } satisfies BillingOutletContext} />
            </div>
          </Col>
        </Row>
      </Container>

      <CreateInvoiceModal
        isVisible={hooks.showCreateInvoiceModal}
        onClose={hooks.handleCloseCreateInvoiceModal}
        ledgerEntries={hooks.ledgerEntries}
        initialSelectedEntry={hooks.initialSelectedEntry}
      />

      <AddInvoiceModal
        isVisible={hooks.showAddInvoiceModal}
        closeModal={hooks.handleCloseAddInvoiceModal}
      />
    </>
  )
}

export default BillingComponent
